import { supabase } from '@shared/config/supabase';
import {
  IFlashcardRepository,
  CourseWithLessons,
  LessonContent,
  CreateDeckParams,
  CreateCardParams,
  UpdateCardParams,
  MergeDecksParams,
  SubmitReviewParams,
  DeckReviewStats,
} from '../../domain/repositories/IFlashcardRepository';
import { FlashcardDeck } from '../../domain/entities/FlashcardDeck';
import { Flashcard } from '../../domain/entities/Flashcard';
import { FlashcardReview } from '../../domain/entities/FlashcardReview';
import { FlashcardDeckMapper } from '../../application/mappers/FlashcardDeckMapper';
import { FlashcardMapper } from '../../application/mappers/FlashcardMapper';
import { FlashcardReviewMapper } from '../../application/mappers/FlashcardReviewMapper';

export class SupabaseFlashcardRepository implements IFlashcardRepository {
  // ============ Course Queries ============

  async getUserCourses(userId: string): Promise<CourseWithLessons[]> {
    // Get enrolled course IDs
    const { data: enrollments } = await supabase
      .from('Enrollments')
      .select('course_id')
      .eq('student_id', userId);

    // Get teaching course IDs
    const { data: teachingCourses } = await supabase
      .from('Courses')
      .select('id')
      .eq('teacher_id', userId);

    const courseIds = [
      ...(enrollments?.map(e => e.course_id) || []),
      ...(teachingCourses?.map(c => c.id) || []),
    ];

    if (courseIds.length === 0) return [];

    // Fetch courses with subject names and lessons
    const { data: courses, error } = await supabase
      .from('Courses')
      .select(`
        id,
        subject:Subjects!inner ( name ),
        lessons:Lessons ( id, title )
      `)
      .in('id', courseIds)
      .order('id', { ascending: true });

    if (error || !courses) {
      console.error('Error fetching user courses:', error);
      return [];
    }

    return courses.map((c: Record<string, unknown>) => ({
      id: String((c as { id: number }).id),
      name: ((c as { subject: { name: string } }).subject as { name: string }).name,
      lessons: Array.isArray(c.lessons)
        ? (c.lessons as { id: number; title: string }[]).map(l => ({ id: String(l.id), title: l.title }))
        : [],
    }));
  }

  async getLessonContent(lessonId: string): Promise<LessonContent | null> {
    // 1. Get lesson data (title, content_body, content_type, ai_summary)
    const { data: lesson, error } = await supabase
      .from('Lessons')
      .select('title, content_type, content_body, ai_summary')
      .eq('id', lessonId)
      .single();

    if (error || !lesson) {
      console.error('Error fetching lesson content:', error);
      return null;
    }

    // 2. For VIDEO lessons, fetch transcription via: content_body (mux URL) → videos → chunks
    let transcription: string | null = null;
    if (lesson.content_type === 'VIDEO' && lesson.content_body) {
      // Extract Mux playback ID from URL like https://stream.mux.com/{playbackId}.m3u8
      const muxMatch = lesson.content_body.match(/stream\.mux\.com\/([^.]+)/);
      if (muxMatch) {
        const playbackId = muxMatch[1];
        // Find the video by its Mux playback ID
        const { data: video } = await supabase
          .from('videos')
          .select('id')
          .eq('provider_video_id', playbackId)
          .single();

        if (video) {
          const { data: chunks } = await supabase
            .from('video_subtitle_chunks')
            .select('chunk_text')
            .eq('video_id', video.id)
            .order('chunk_index', { ascending: true });

          if (chunks && chunks.length > 0) {
            transcription = chunks.map((c: { chunk_text: string }) => c.chunk_text).join(' ');
          }
        }
      }
    }

    return {
      title: lesson.title,
      contentType: lesson.content_type,
      contentBody: lesson.content_body || '',
      aiSummary: lesson.ai_summary || null,
      transcription,
    };
  }

  // ============ Deck Operations ============

  async findDeckById(id: string): Promise<FlashcardDeck | null> {
    const { data, error } = await supabase
      .from('FlashcardDecks')
      .select(`
        *,
        course:Courses!inner (
          id,
          subject:Subjects!inner ( name )
        ),
        lesson:Lessons ( title ),
        creator:users!FlashcardDecks_created_by_fkey ( full_name )
      `)
      .eq('id', id)
      .single();

    if (error || !data) {
      console.error('Error fetching deck:', error);
      return null;
    }

    return FlashcardDeckMapper.fromDatabaseRow(data);
  }

  async findDecksByUserId(userId: string): Promise<FlashcardDeck[]> {
    // Get user's enrolled course IDs
    const { data: enrollments } = await supabase
      .from('Enrollments')
      .select('course_id')
      .eq('student_id', userId);

    // Get user's teaching course IDs
    const { data: teachingCourses } = await supabase
      .from('Courses')
      .select('id')
      .eq('teacher_id', userId);

    const courseIds = [
      ...(enrollments?.map(e => e.course_id) || []),
      ...(teachingCourses?.map(c => c.id) || []),
    ];

    if (courseIds.length === 0) return [];

    const { data, error } = await supabase
      .from('FlashcardDecks')
      .select(`
        *,
        course:Courses!inner (
          id,
          subject:Subjects!inner ( name )
        ),
        lesson:Lessons ( title ),
        creator:users!FlashcardDecks_created_by_fkey ( full_name )
      `)
      .in('course_id', courseIds)
      .order('updated_at', { ascending: false });

    if (error || !data) {
      console.error('Error fetching decks:', error);
      return [];
    }

    return data.map(row => FlashcardDeckMapper.fromDatabaseRow(row));
  }

  async findDecksByCourseId(courseId: string): Promise<FlashcardDeck[]> {
    const { data, error } = await supabase
      .from('FlashcardDecks')
      .select(`
        *,
        course:Courses!inner (
          id,
          subject:Subjects!inner ( name )
        ),
        lesson:Lessons ( title ),
        creator:users!FlashcardDecks_created_by_fkey ( full_name )
      `)
      .eq('course_id', courseId)
      .order('updated_at', { ascending: false });

    if (error || !data) {
      console.error('Error fetching decks by course:', error);
      return [];
    }

    return data.map(row => FlashcardDeckMapper.fromDatabaseRow(row));
  }

  async findDeckByLessonId(lessonId: string, createdBy: string): Promise<FlashcardDeck | null> {
    const { data, error } = await supabase
      .from('FlashcardDecks')
      .select('*')
      .eq('lesson_id', lessonId)
      .eq('created_by', createdBy)
      .maybeSingle();

    if (error || !data) return null;
    return FlashcardDeckMapper.fromDatabaseRow(data);
  }

  async createDeck(params: CreateDeckParams): Promise<FlashcardDeck> {
    const { data, error } = await supabase
      .from('FlashcardDecks')
      .insert({
        lesson_id: params.lessonId || null,
        course_id: Number(params.courseId),
        created_by: params.createdBy,
        creator_role: params.creatorRole,
        workspace_id: Number(params.workspaceId),
        title: params.title,
        description: params.description || '',
        is_ai_generated: params.isAiGenerated || false,
      })
      .select()
      .single();

    if (error || !data) {
      console.error('Error creating deck:', error);
      throw new Error('Error al crear el mazo');
    }

    return FlashcardDeckMapper.fromDatabaseRow(data);
  }

  async updateDeck(id: string, title: string, description?: string): Promise<FlashcardDeck> {
    const updateData: Record<string, unknown> = {
      title,
      updated_at: new Date().toISOString(),
    };
    if (description !== undefined) {
      updateData.description = description;
    }

    const { data, error } = await supabase
      .from('FlashcardDecks')
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error || !data) {
      console.error('Error updating deck:', error);
      throw new Error('Error al actualizar el mazo');
    }

    return FlashcardDeckMapper.fromDatabaseRow(data);
  }

  async deleteDeck(id: string): Promise<void> {
    const { error } = await supabase
      .from('FlashcardDecks')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting deck:', error);
      throw new Error('Error al eliminar el mazo');
    }
  }

  async mergeDecks(params: MergeDecksParams): Promise<FlashcardDeck> {
    // 1. Create the merged deck
    const { data: newDeck, error: deckError } = await supabase
      .from('FlashcardDecks')
      .insert({
        course_id: Number(params.courseId),
        created_by: params.createdBy,
        creator_role: params.creatorRole,
        workspace_id: Number(params.workspaceId),
        title: params.title,
        is_merged: true,
        source_deck_ids: params.sourceDeckIds.map(Number),
      })
      .select()
      .single();

    if (deckError || !newDeck) {
      console.error('Error creating merged deck:', deckError);
      throw new Error('Error al crear el mazo combinado');
    }

    // 2. Copy cards from source decks
    const { data: sourceCards, error: cardsError } = await supabase
      .from('Flashcards')
      .select('front, back, tags')
      .in('deck_id', params.sourceDeckIds.map(Number))
      .order('sort_order');

    if (cardsError) {
      console.error('Error fetching source cards:', cardsError);
      throw new Error('Error al obtener las tarjetas de los mazos fuente');
    }

    if (sourceCards && sourceCards.length > 0) {
      const cardsToInsert = sourceCards.map((card, index) => ({
        deck_id: newDeck.id,
        front: card.front,
        back: card.back,
        tags: card.tags || [],
        sort_order: index,
      }));

      const { error: insertError } = await supabase
        .from('Flashcards')
        .insert(cardsToInsert);

      if (insertError) {
        console.error('Error inserting merged cards:', insertError);
        throw new Error('Error al copiar las tarjetas');
      }
    }

    return FlashcardDeckMapper.fromDatabaseRow(newDeck);
  }

  // ============ Card Operations ============

  async findCardById(id: string): Promise<Flashcard | null> {
    const { data, error } = await supabase
      .from('Flashcards')
      .select('*')
      .eq('id', id)
      .single();

    if (error || !data) return null;
    return FlashcardMapper.fromDatabaseRow(data);
  }

  async findCardsByDeckId(deckId: string): Promise<Flashcard[]> {
    const { data, error } = await supabase
      .from('Flashcards')
      .select('*')
      .eq('deck_id', deckId)
      .order('sort_order');

    if (error || !data) {
      console.error('Error fetching cards:', error);
      return [];
    }

    return data.map(row => FlashcardMapper.fromDatabaseRow(row));
  }

  async createCard(params: CreateCardParams): Promise<Flashcard> {
    const { data, error } = await supabase
      .from('Flashcards')
      .insert({
        deck_id: Number(params.deckId),
        front: params.front,
        back: params.back,
        sort_order: params.sortOrder ?? 0,
        tags: params.tags || [],
      })
      .select()
      .single();

    if (error || !data) {
      console.error('Error creating card:', error);
      throw new Error('Error al crear la tarjeta');
    }

    return FlashcardMapper.fromDatabaseRow(data);
  }

  async updateCard(params: UpdateCardParams): Promise<Flashcard> {
    const updateData: Record<string, unknown> = {
      updated_at: new Date().toISOString(),
    };
    if (params.front !== undefined) updateData.front = params.front;
    if (params.back !== undefined) updateData.back = params.back;
    if (params.sortOrder !== undefined) updateData.sort_order = params.sortOrder;
    if (params.tags !== undefined) updateData.tags = params.tags;

    const { data, error } = await supabase
      .from('Flashcards')
      .update(updateData)
      .eq('id', params.id)
      .select()
      .single();

    if (error || !data) {
      console.error('Error updating card:', error);
      throw new Error('Error al actualizar la tarjeta');
    }

    return FlashcardMapper.fromDatabaseRow(data);
  }

  async deleteCard(id: string): Promise<void> {
    const { error } = await supabase
      .from('Flashcards')
      .delete()
      .eq('id', id);

    if (error) {
      console.error('Error deleting card:', error);
      throw new Error('Error al eliminar la tarjeta');
    }
  }

  async reorderCards(deckId: string, cardIds: string[]): Promise<void> {
    for (let i = 0; i < cardIds.length; i++) {
      await supabase
        .from('Flashcards')
        .update({ sort_order: i })
        .eq('id', cardIds[i])
        .eq('deck_id', deckId);
    }
  }

  // ============ Review Operations ============

  async findReviewByCardAndStudent(
    flashcardId: string,
    studentId: string
  ): Promise<FlashcardReview | null> {
    const { data, error } = await supabase
      .from('FlashcardReviews')
      .select('*')
      .eq('flashcard_id', flashcardId)
      .eq('student_id', studentId)
      .maybeSingle();

    if (error || !data) return null;
    return FlashcardReviewMapper.fromDatabaseRow(data);
  }

  async findDueCards(deckId: string, studentId: string, limit = 20): Promise<Flashcard[]> {
    const { data, error } = await supabase
      .rpc('get_due_flashcards', {
        p_deck_id: Number(deckId),
        p_student_id: studentId,
        p_limit: limit,
      });

    if (error || !data) {
      console.error('Error fetching due cards:', error);
      return [];
    }

    return data.map((row: Record<string, unknown>) => FlashcardMapper.fromDatabaseRow(row));
  }

  async submitReview(params: SubmitReviewParams): Promise<FlashcardReview> {
    const { data, error } = await supabase
      .from('FlashcardReviews')
      .upsert(
        {
          flashcard_id: Number(params.flashcardId),
          student_id: params.studentId,
          easiness_factor: params.easinessFactor,
          interval_days: params.intervalDays,
          repetitions: params.repetitions,
          next_review_date: params.nextReviewDate.toISOString(),
          last_quality: params.quality,
          last_reviewed_at: new Date().toISOString(),
          total_reviews: undefined, // Will be incremented by trigger or manual
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'flashcard_id,student_id' }
      )
      .select()
      .single();

    if (error || !data) {
      console.error('Error submitting review:', error);
      throw new Error('Error al enviar la revisión');
    }

    // Increment total_reviews manually since upsert doesn't support increment
    await supabase.rpc('increment_flashcard_review_count', {
      p_flashcard_id: Number(params.flashcardId),
      p_student_id: params.studentId,
    });

    return FlashcardReviewMapper.fromDatabaseRow(data);
  }

  async getDeckReviewStats(deckId: string, studentId: string): Promise<DeckReviewStats> {
    // Get total cards in deck
    const { count: totalCards } = await supabase
      .from('Flashcards')
      .select('*', { count: 'exact', head: true })
      .eq('deck_id', deckId);

    // Get reviews for this student in this deck
    const { data: reviews } = await supabase
      .from('FlashcardReviews')
      .select(`
        easiness_factor,
        interval_days,
        next_review_date,
        total_reviews,
        flashcard:Flashcards!inner ( deck_id )
      `)
      .eq('student_id', studentId)
      .eq('flashcard.deck_id', deckId);

    const now = new Date();
    let dueCards = 0;
    let masteredCards = 0;
    let totalEasiness = 0;
    const reviewedCardIds = new Set<string>();

    if (reviews) {
      for (const review of reviews) {
        reviewedCardIds.add(String((review as Record<string, unknown>).flashcard_id ?? review));
        if (new Date(review.next_review_date) <= now) dueCards++;
        if (review.interval_days >= 21) masteredCards++;
        totalEasiness += review.easiness_factor;
      }
    }

    const reviewCount = reviews?.length || 0;
    const newCards = (totalCards || 0) - reviewCount;

    return {
      totalCards: totalCards || 0,
      dueCards: dueCards + newCards, // New cards are always "due"
      newCards,
      masteredCards,
      averageEasiness: reviewCount > 0 ? totalEasiness / reviewCount : 2.5,
    };
  }

  async getStudyStreak(studentId: string): Promise<number> {
    const { data, error } = await supabase
      .from('FlashcardReviews')
      .select('last_reviewed_at')
      .eq('student_id', studentId)
      .not('last_reviewed_at', 'is', null)
      .order('last_reviewed_at', { ascending: false });

    if (error || !data || data.length === 0) return 0;

    // Count consecutive days with reviews backwards from today
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const reviewDates = new Set(
      data.map(r => {
        const d = new Date(r.last_reviewed_at);
        d.setHours(0, 0, 0, 0);
        return d.toISOString();
      })
    );

    let streak = 0;
    const checkDate = new Date(today);

    while (reviewDates.has(checkDate.toISOString())) {
      streak++;
      checkDate.setDate(checkDate.getDate() - 1);
    }

    return streak;
  }

  // ============ Analytics (Admin) ============

  async getFlashcardAnalytics() {
    // Fetch all decks with workspace info
    const { data: decks, error: decksError } = await supabase
      .from('FlashcardDecks')
      .select(`
        id,
        workspace_id,
        card_count,
        is_ai_generated,
        course:Courses!inner ( id, subject:Subjects!inner ( name ) )
      `);

    if (decksError) throw decksError;

    // Fetch all reviews
    const { data: reviews, error: reviewsError } = await supabase
      .from('FlashcardReviews')
      .select('id, student_id, easiness_factor, total_reviews, flashcard_id');

    if (reviewsError) throw reviewsError;

    // Fetch workspaces for names
    const { data: workspaces, error: wsError } = await supabase
      .from('Workspaces')
      .select('id, name');

    if (wsError) throw wsError;

    const workspaceMap = new Map(workspaces?.map(w => [String(w.id), w.name]) || []);

    // Compute totals
    const totalDecks = decks?.length || 0;
    const totalCards = decks?.reduce((sum, d) => sum + (d.card_count || 0), 0) || 0;
    const aiDecks = decks?.filter(d => d.is_ai_generated) || [];
    const aiGeneratedCards = aiDecks.reduce((sum, d) => sum + (d.card_count || 0), 0);
    const manualCards = totalCards - aiGeneratedCards;

    const uniqueStudents = new Set(reviews?.map(r => r.student_id) || []);
    const activeStudents = uniqueStudents.size;

    const avgEF = reviews && reviews.length > 0
      ? reviews.reduce((sum, r) => sum + (r.easiness_factor || 2.5), 0) / reviews.length
      : 2.5;
    // Convert EF to a retention percentage (EF 2.5 = ~80%, EF 1.3 = ~40%)
    const averageRetention = Math.round(Math.min(100, Math.max(0, ((avgEF - 1.3) / (2.5 - 1.3)) * 60 + 40)));

    // Per-workspace stats
    const wsStatsMap = new Map<string, {
      deckCount: number;
      cardCount: number;
      studentIds: Set<string>;
      totalReviews: number;
      efSum: number;
      efCount: number;
    }>();

    for (const deck of decks || []) {
      const wsId = String(deck.workspace_id);
      if (!wsStatsMap.has(wsId)) {
        wsStatsMap.set(wsId, {
          deckCount: 0,
          cardCount: 0,
          studentIds: new Set(),
          totalReviews: 0,
          efSum: 0,
          efCount: 0,
        });
      }
      const stats = wsStatsMap.get(wsId)!;
      stats.deckCount++;
      stats.cardCount += deck.card_count || 0;
    }

    // Add review data to workspace stats (via flashcard → deck mapping)
    const deckWorkspaceMap = new Map<string, string>();
    for (const deck of decks || []) {
      deckWorkspaceMap.set(String(deck.id), String(deck.workspace_id));
    }

    // We need flashcard → deck mapping for reviews
    const { data: flashcards } = await supabase
      .from('Flashcards')
      .select('id, deck_id');

    const flashcardDeckMap = new Map<string, string>();
    for (const fc of flashcards || []) {
      flashcardDeckMap.set(String(fc.id), String(fc.deck_id));
    }

    for (const review of reviews || []) {
      const deckId = flashcardDeckMap.get(String(review.flashcard_id));
      if (!deckId) continue;
      const wsId = deckWorkspaceMap.get(deckId);
      if (!wsId || !wsStatsMap.has(wsId)) continue;
      const stats = wsStatsMap.get(wsId)!;
      stats.studentIds.add(review.student_id);
      stats.totalReviews += review.total_reviews || 0;
      stats.efSum += review.easiness_factor || 2.5;
      stats.efCount++;
    }

    const workspaceStats = Array.from(wsStatsMap.entries()).map(([wsId, stats]) => ({
      workspaceId: wsId,
      workspaceName: workspaceMap.get(wsId) || 'Unknown',
      deckCount: stats.deckCount,
      cardCount: stats.cardCount,
      activeStudents: stats.studentIds.size,
      totalReviews: stats.totalReviews,
      retentionRate: stats.efCount > 0
        ? Math.round(Math.min(100, Math.max(0, ((stats.efSum / stats.efCount - 1.3) / (2.5 - 1.3)) * 60 + 40)))
        : 0,
    }));

    // Top difficult cards (lowest average easiness)
    const cardEFMap = new Map<string, { efSum: number; count: number; flashcardId: string }>();
    for (const review of reviews || []) {
      const fcId = String(review.flashcard_id);
      if (!cardEFMap.has(fcId)) {
        cardEFMap.set(fcId, { efSum: 0, count: 0, flashcardId: fcId });
      }
      const entry = cardEFMap.get(fcId)!;
      entry.efSum += review.easiness_factor || 2.5;
      entry.count++;
    }

    const sortedCards = Array.from(cardEFMap.values())
      .filter(c => c.count >= 3) // Only cards with enough reviews
      .map(c => ({ ...c, avgEF: c.efSum / c.count }))
      .sort((a, b) => a.avgEF - b.avgEF)
      .slice(0, 10);

    // Fetch card details for top difficult
    const topDifficultCards: {
      id: string;
      front: string;
      back: string;
      deckTitle: string;
      averageEasiness: number;
      totalReviews: number;
    }[] = [];

    if (sortedCards.length > 0) {
      const cardIds = sortedCards.map(c => c.flashcardId);
      const { data: cardDetails } = await supabase
        .from('Flashcards')
        .select('id, front, back, deck:FlashcardDecks!inner ( title )')
        .in('id', cardIds);

      for (const sc of sortedCards) {
        const detail = cardDetails?.find(cd => String(cd.id) === sc.flashcardId);
        if (detail) {
          const deckData = detail.deck as unknown as { title: string };
          topDifficultCards.push({
            id: String(detail.id),
            front: detail.front,
            back: detail.back,
            deckTitle: deckData?.title || '',
            averageEasiness: Math.round(sc.avgEF * 100) / 100,
            totalReviews: sc.count,
          });
        }
      }
    }

    return {
      totalDecks,
      totalCards,
      aiGeneratedCards,
      manualCards,
      activeStudents,
      averageRetention,
      workspaceStats,
      topDifficultCards,
    };
  }
}
